import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  X, Save, Send, Upload, Trash2, Check, FileText, User, GraduationCap,
  Briefcase, MapPin, Heart, Shield, FileSignature, Stethoscope, Syringe,
  Gavel, ClipboardCheck, Image as ImageIcon, ChevronLeft, ChevronRight, AlertCircle,
  Award, BookOpen, TrendingUp,
} from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';
import SignaturePad from './SignaturePad';

// ═══════════════════════════════════════════════════════════════════
//  OnboardingFormModal — multi-section paper-form digitisation.
//  Staff fills each section; HR verifies via OnboardingHrPanel.
//
//  Sections follow the clinic's HR file exactly (profile → medical →
//  affidavits → credentials). Each section supports partial save, so
//  staff can come back and continue.
// ═══════════════════════════════════════════════════════════════════

const SECTIONS = [
  { key: 'profile',                  label: 'Profile Sheet',            icon: User,           owner: 'staff' },
  { key: 'addresses',                label: 'Address & Emergency',      icon: MapPin,         owner: 'staff' },
  { key: 'educations',               label: 'Education',                icon: GraduationCap,  owner: 'staff' },
  { key: 'workHistory',              label: 'Work Experience',          icon: Briefcase,      owner: 'staff' },
  { key: 'languages',                label: 'Languages & Hobbies',      icon: Heart,          owner: 'staff' },
  { key: 'otherDetails',             label: 'Other Details',            icon: FileText,       owner: 'staff' },
  { key: 'appointmentLetter',        label: 'Appointment Letter',       icon: FileSignature,  owner: 'staff' },
  { key: 'confidentialityAgreement', label: 'Confidentiality & NDA',    icon: Shield,         owner: 'staff' },
  { key: 'joiningInduction',         label: 'Joining & Induction',      icon: ClipboardCheck, owner: 'staff' },
  { key: 'authorizationLetter',      label: 'Background-Check Auth',    icon: Gavel,          owner: 'staff' },
  { key: 'bloodTestConsent',         label: 'Blood Test Consent',       icon: Syringe,        owner: 'staff' },
  { key: 'healthCheckup',            label: 'Annual Health Check-Up',   icon: Stethoscope,    owner: 'physician' },
  { key: 'medicalCertificate',       label: 'Medical Certificate',      icon: Stethoscope,    owner: 'physician' },
  { key: 'hepatitisBDeclaration',    label: 'Hepatitis-B Declaration',  icon: Syringe,        owner: 'staff' },
  { key: 'noCriminalAffidavit',      label: 'No-Criminal Affidavit',    icon: Gavel,          owner: 'staff' },
  { key: 'employerReferenceCheck',   label: 'Ex-Employer Reference',    icon: FileText,       owner: 'hr' },
  { key: 'telephonicReferenceCheck', label: 'Telephonic Ref Check',     icon: FileText,       owner: 'hr' },
  { key: 'credentialsVerification',  label: 'Credentials Verification', icon: Check,          owner: 'staff+hr' },
  { key: 'jobDescription',           label: 'Job Description',          icon: BookOpen,       owner: 'staff+hr' },
  { key: 'trainingLog',              label: 'Training Log',             icon: GraduationCap,  owner: 'hr' },
  { key: 'appraisalLetter',          label: 'Appraisal Letter',         icon: TrendingUp,     owner: 'hr' },
  { key: 'performanceAppraisal',     label: 'Performance Appraisal',    icon: Award,          owner: 'hr' },
  { key: 'attachments',              label: 'Attached Reports',         icon: ImageIcon,      owner: 'staff' },
];

// Constant keys for the performance-appraisal rating table
const PA_PERSONAL = [
  ['leadership',    'Leadership',    'Directs operation activity and performance of subordinates'],
  ['judgement',     'Judgement',     'Analyses problems, evaluates alternatives and selects best course'],
  ['management',    'The Management','Adheres to responsibility in a firmly manner'],
  ['creativity',    'Creativity',    'Generates workable & innovative ideas and techniques'],
  ['communication', 'Communication', 'Effectively listens, conveys and receives information'],
];
const PA_ORG = [
  ['workHabits',        'Work Habits',        'Conducts him/herself well in the work environment'],
  ['loyalty',           'Loyalty',            'Is aware of the organizational goals and respects them'],
  ['consensusBuilding', 'Consensus Building', 'Builds & maintains work relationships'],
  ['integrity',         'Integrity',          'Has a deep sense of honesty and truthfulness'],
  ['staffDevelopment',  'Staff Development',  'Provides opportunities to challenge subordinates'],
];
const PA_PRO = [
  ['jobKnowledge',          'Job Knowledge',              'Technical / administrative / managerial knowledge'],
  ['attendance',            'Attendance',                 'Can be depended upon to fulfil position responsibility'],
  ['politeness',            'Politeness & Courtesy',      'Represents the organization in a positive manner'],
  ['professionalCompetence','Professional Competence',    'Is a source of supply and support to organization'],
  ['patientFocus',          'Patient Focus',              'Takes initiatives to meet the customer needs'],
  ['managementSkills',      'Management Skills',          'Demonstrates effective management abilities'],
  ['planningAndOrganizing', 'Planning and Organizing',    'Plans, organizes and implements tasks'],
  ['problemSolving',        'Problem Solving & Decision', 'Clearly isolates, defines and seeks solutions'],
  ['interpersonalRelations','Interpersonal Relations',    'Shows understanding and sensitivity to others'],
  ['delegation',            'Delegation & Supervisory',   'Ability to authorize work and supervise subordinates'],
];
const PA_GRADES = [
  { min: 121, max: 150, grade: 'A', label: 'Outstanding', color: '#16a34a' },
  { min: 91,  max: 120, grade: 'B', label: 'Exceeds standard', color: '#0891b2' },
  { min: 61,  max: 90,  grade: 'C', label: 'Meets requirement', color: '#f59e0b' },
  { min: 31,  max: 60,  grade: 'D', label: 'Minimum', color: '#f97316' },
  { min: 0,   max: 30,  grade: 'E', label: 'Unsatisfactory', color: '#dc2626' },
];

const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: theme.textMuted, margin: '10px 0 4px' };
const row = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };
const row3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 };
const smallInput = { ...inputStyle, padding: '7px 10px' };
const chipBtn = (active, color) => ({
  padding: '6px 12px',
  borderRadius: 6,
  border: `1px solid ${active ? color : theme.border}`,
  background: active ? color + '22' : 'white',
  color: active ? color : theme.textMuted,
  fontWeight: 600, fontSize: 12, cursor: 'pointer',
});

const OnboardingFormModal = ({ API_BASE, currentUser, record, onClose, onUpdate }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [form, setForm] = useState(record?.form || {});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [sigFor, setSigFor] = useState(null);    // 'appointment.employee' etc
  const [busyUpload, setBusyUpload] = useState(false);

  useEffect(() => { setForm(record?.form || {}); }, [record?._id]);

  const section = SECTIONS[activeIdx];
  const sectionKey = section.key;
  const isHrSection = section.owner === 'hr' || section.owner === 'physician';

  const show = (t) => { setMsg(t); setTimeout(() => setMsg(''), 2500); };

  const updateSection = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const saveSection = useCallback(async (key, value) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API_BASE}/onboarding/${record._id}/form`, { section: key, value });
      onUpdate && onUpdate(res.data);
      show('Saved');
    } catch (err) {
      show(err.response?.data?.error || err.message);
    } finally { setSaving(false); }
  }, [API_BASE, record?._id, onUpdate]);

  const submitForReview = async () => {
    if (!window.confirm('Form HR ko review ke liye submit kar rahe hain. Iske baad tum edit nahi kar paoge. Continue?')) return;
    try {
      const res = await axios.post(`${API_BASE}/onboarding/${record._id}/submit`);
      onUpdate && onUpdate(res.data);
      show('Submitted for HR review');
    } catch (err) { show(err.response?.data?.error || err.message); }
  };

  const handleFile = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind || 'other');
    fd.append('label', file.name);
    fd.append('uploadedBy', currentUser?.fullName || currentUser?.username || '');
    setBusyUpload(true);
    try {
      const res = await axios.post(`${API_BASE}/onboarding/${record._id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUpdate && onUpdate(res.data.record);
      setForm(res.data.record.form || {});
      show('Uploaded');
    } catch (err) { show(err.response?.data?.error || err.message); }
    finally { setBusyUpload(false); e.target.value = ''; }
  };

  const removeAttachment = async (idx) => {
    if (!window.confirm('Delete karein?')) return;
    try {
      const res = await axios.delete(`${API_BASE}/onboarding/${record._id}/attachment/${idx}`);
      onUpdate && onUpdate(res.data);
      setForm(res.data.form || {});
    } catch (err) { show(err.response?.data?.error || err.message); }
  };

  // ─── SIGNATURE helpers ───
  const saveSig = (data) => {
    const [secKey, field] = (sigFor || '').split('.');
    if (!secKey || !field) { setSigFor(null); return; }
    const next = { ...(form[secKey] || {}), [field]: data };
    setForm(prev => ({ ...prev, [secKey]: next }));
    saveSection(secKey, next);
    setSigFor(null);
  };

  const sigButton = (sec, field, label) => {
    const existing = form[sec]?.[field];
    return (
      <div style={{ marginTop: 8 }}>
        <label style={lbl}>{label}</label>
        {existing && (
          <img src={existing} alt="sig" style={{ maxHeight: 60, border: '1px solid ' + theme.border, borderRadius: 6, marginBottom: 4, background: 'white' }}/>
        )}
        <div>
          <button type="button" onClick={() => setSigFor(`${sec}.${field}`)} style={{ ...btnStyle, padding: '6px 12px', fontSize: 12 }}>
            {existing ? 'Re-sign' : 'Sign here'}
          </button>
        </div>
      </div>
    );
  };

  // ─── SECTION BODIES ───
  const p = form.profile || {};
  const setP = (field, val) => updateSection('profile', { ...p, [field]: val });

  const renderProfile = () => (
    <>
      <div style={row}>
        <div><label style={lbl}>Position applied for</label>
          <input style={inputStyle} value={p.position || ''} onChange={e => setP('position', e.target.value)}/></div>
        <div><label style={lbl}>Location / Branch</label>
          <input style={inputStyle} value={p.location || ''} onChange={e => setP('location', e.target.value)}/></div>
      </div>
      <div style={row3}>
        <div><label style={lbl}>First name</label>
          <input style={inputStyle} value={p.firstName || ''} onChange={e => setP('firstName', e.target.value)}/></div>
        <div><label style={lbl}>Middle name</label>
          <input style={inputStyle} value={p.middleName || ''} onChange={e => setP('middleName', e.target.value)}/></div>
        <div><label style={lbl}>Last name</label>
          <input style={inputStyle} value={p.lastName || ''} onChange={e => setP('lastName', e.target.value)}/></div>
      </div>
      <div style={row3}>
        <div><label style={lbl}>Gender</label>
          <select style={inputStyle} value={p.gender || ''} onChange={e => setP('gender', e.target.value)}>
            <option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
          </select></div>
        <div><label style={lbl}>Date of birth</label>
          <input type="date" style={inputStyle} value={p.dob || ''} onChange={e => setP('dob', e.target.value)}/></div>
        <div><label style={lbl}>Blood group</label>
          <input style={inputStyle} placeholder="B+" value={p.bloodGroup || ''} onChange={e => setP('bloodGroup', e.target.value)}/></div>
      </div>
      <div style={row3}>
        <div><label style={lbl}>Home town</label>
          <input style={inputStyle} value={p.homeTown || ''} onChange={e => setP('homeTown', e.target.value)}/></div>
        <div><label style={lbl}>Nationality</label>
          <input style={inputStyle} value={p.nationality || 'Indian'} onChange={e => setP('nationality', e.target.value)}/></div>
        <div><label style={lbl}>Religion</label>
          <input style={inputStyle} value={p.religion || ''} onChange={e => setP('religion', e.target.value)}/></div>
      </div>
      <div style={row3}>
        <div><label style={lbl}>Marital status</label>
          <select style={inputStyle} value={p.maritalStatus || ''} onChange={e => setP('maritalStatus', e.target.value)}>
            <option value="">—</option><option value="single">Single</option><option value="married">Married</option>
            <option value="divorced">Divorced</option><option value="widowed">Widowed</option>
          </select></div>
        <div><label style={lbl}>Wedding date</label>
          <input type="date" style={inputStyle} value={p.weddingDate || ''} onChange={e => setP('weddingDate', e.target.value)}/></div>
        <div><label style={lbl}># Children</label>
          <input style={inputStyle} value={p.numChildren || ''} onChange={e => setP('numChildren', e.target.value)}/></div>
      </div>

      <h4 style={{ marginTop: 18, color: theme.navy }}>Family</h4>
      {['mother','father','spouse'].map(rel => (
        <div key={rel} style={{ ...row3, marginTop: 6 }}>
          <div><label style={lbl}>{rel[0].toUpperCase() + rel.slice(1)} name</label>
            <input style={smallInput} value={p[rel]?.name || ''} onChange={e => setP(rel, { ...(p[rel] || {}), name: e.target.value })}/></div>
          <div><label style={lbl}>Date of birth</label>
            <input style={smallInput} placeholder="dd-mm-yy" value={p[rel]?.dob || ''} onChange={e => setP(rel, { ...(p[rel] || {}), dob: e.target.value })}/></div>
          <div><label style={lbl}>Occupation</label>
            <input style={smallInput} value={p[rel]?.occupation || ''} onChange={e => setP(rel, { ...(p[rel] || {}), occupation: e.target.value })}/></div>
        </div>
      ))}

      <h4 style={{ marginTop: 18, color: theme.navy }}>Identity</h4>
      <div style={row3}>
        <div><label style={lbl}>PAN No</label>
          <input style={inputStyle} value={p.panNo || ''} onChange={e => setP('panNo', e.target.value)}/></div>
        <div><label style={lbl}>Aadhaar No</label>
          <input style={inputStyle} value={p.aadharNo || ''} onChange={e => setP('aadharNo', e.target.value)}/></div>
        <div><label style={lbl}>Passport No</label>
          <input style={inputStyle} value={p.passportNo || ''} onChange={e => setP('passportNo', e.target.value)}/></div>
      </div>

      <h4 style={{ marginTop: 18, color: theme.navy }}>Professional Registrations</h4>
      {(form.professionalRegs || []).map((reg, i) => (
        <div key={i} style={{ ...row3, marginBottom: 6, position: 'relative', paddingRight: 28 }}>
          <input style={smallInput} placeholder="Council / Body" value={reg.name || ''} onChange={e => {
            const next = [...(form.professionalRegs || [])]; next[i] = { ...reg, name: e.target.value }; updateSection('professionalRegs', next);
          }}/>
          <input style={smallInput} placeholder="Registration No" value={reg.number || ''} onChange={e => {
            const next = [...(form.professionalRegs || [])]; next[i] = { ...reg, number: e.target.value }; updateSection('professionalRegs', next);
          }}/>
          <input style={smallInput} placeholder="Valid till" value={reg.validTill || ''} onChange={e => {
            const next = [...(form.professionalRegs || [])]; next[i] = { ...reg, validTill: e.target.value }; updateSection('professionalRegs', next);
          }}/>
          <button onClick={() => { const next = [...(form.professionalRegs || [])]; next.splice(i, 1); updateSection('professionalRegs', next); }}
            style={{ position: 'absolute', right: 0, top: 4, background: 'none', border: 'none', cursor: 'pointer', color: theme.danger }}>
            <Trash2 size={14}/>
          </button>
        </div>
      ))}
      <button type="button" onClick={() => updateSection('professionalRegs', [...(form.professionalRegs || []), {}])}
        style={{ ...btnStyle, padding: '6px 12px', fontSize: 12, marginTop: 4 }}>+ Add registration</button>
    </>
  );

  const renderAddresses = () => {
    const pres = form.presentAddress || {};
    const perm = form.permanentAddress || {};
    const ec = form.emergencyContact || {};
    const setPres = (f, v) => updateSection('presentAddress', { ...pres, [f]: v });
    const setPerm = (f, v) => updateSection('permanentAddress', { ...perm, [f]: v });
    const setEc   = (f, v) => updateSection('emergencyContact', { ...ec, [f]: v });
    return (
      <>
        <h4 style={{ marginTop: 6, color: theme.navy }}>Present Address</h4>
        <div style={row}>
          <div><label style={lbl}>House No</label><input style={inputStyle} value={pres.houseNo || ''} onChange={e => setPres('houseNo', e.target.value)}/></div>
          <div><label style={lbl}>Street / Locality</label><input style={inputStyle} value={pres.street || ''} onChange={e => setPres('street', e.target.value)}/></div>
        </div>
        <div style={row3}>
          <div><label style={lbl}>City</label><input style={inputStyle} value={pres.city || ''} onChange={e => setPres('city', e.target.value)}/></div>
          <div><label style={lbl}>State</label><input style={inputStyle} value={pres.state || ''} onChange={e => setPres('state', e.target.value)}/></div>
          <div><label style={lbl}>Pin Code</label><input style={inputStyle} value={pres.pincode || ''} onChange={e => setPres('pincode', e.target.value)}/></div>
        </div>
        <div style={row3}>
          <div><label style={lbl}>Phone</label><input style={inputStyle} value={pres.phone || ''} onChange={e => setPres('phone', e.target.value)}/></div>
          <div><label style={lbl}>Mobile</label><input style={inputStyle} value={pres.mobile || ''} onChange={e => setPres('mobile', e.target.value)}/></div>
          <div><label style={lbl}>Email</label><input style={inputStyle} value={pres.email || ''} onChange={e => setPres('email', e.target.value)}/></div>
        </div>

        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
          <h4 style={{ margin: 0, color: theme.navy }}>Permanent Address</h4>
          <label style={{ fontSize: 12, color: theme.textMuted }}>
            <input type="checkbox" checked={!!form.sameAsPresent} onChange={e => {
              updateSection('sameAsPresent', e.target.checked);
              if (e.target.checked) updateSection('permanentAddress', { ...pres });
            }}/> Same as present
          </label>
        </div>
        {!form.sameAsPresent && (
          <>
            <div style={row}>
              <div><label style={lbl}>House No</label><input style={inputStyle} value={perm.houseNo || ''} onChange={e => setPerm('houseNo', e.target.value)}/></div>
              <div><label style={lbl}>Street / Locality</label><input style={inputStyle} value={perm.street || ''} onChange={e => setPerm('street', e.target.value)}/></div>
            </div>
            <div style={row3}>
              <div><label style={lbl}>City</label><input style={inputStyle} value={perm.city || ''} onChange={e => setPerm('city', e.target.value)}/></div>
              <div><label style={lbl}>State</label><input style={inputStyle} value={perm.state || ''} onChange={e => setPerm('state', e.target.value)}/></div>
              <div><label style={lbl}>Pin Code</label><input style={inputStyle} value={perm.pincode || ''} onChange={e => setPerm('pincode', e.target.value)}/></div>
            </div>
          </>
        )}

        <h4 style={{ marginTop: 18, color: theme.navy }}>Emergency Contact</h4>
        <div style={row}>
          <div><label style={lbl}>Name</label><input style={inputStyle} value={ec.name || ''} onChange={e => setEc('name', e.target.value)}/></div>
          <div><label style={lbl}>Relationship</label><input style={inputStyle} value={ec.relationship || ''} onChange={e => setEc('relationship', e.target.value)}/></div>
        </div>
        <div style={row3}>
          <div><label style={lbl}>City</label><input style={inputStyle} value={ec.city || ''} onChange={e => setEc('city', e.target.value)}/></div>
          <div><label style={lbl}>State</label><input style={inputStyle} value={ec.state || ''} onChange={e => setEc('state', e.target.value)}/></div>
          <div><label style={lbl}>Mobile</label><input style={inputStyle} value={ec.mobile || ''} onChange={e => setEc('mobile', e.target.value)}/></div>
        </div>
      </>
    );
  };

  const renderEducations = () => (
    <>
      <p style={{ fontSize: 13, color: theme.textMuted, marginBottom: 8 }}>
        Saare qualifications list karo — 10th se highest tak. Certificates upload Attachments section mein.
      </p>
      {(form.educations || []).map((ed, i) => (
        <div key={i} style={{ ...cardStyle, padding: 10, marginBottom: 8, background: theme.sectionBlue || '#f0f9ff' }}>
          <div style={row}>
            <div><label style={lbl}>Qualification</label>
              <input style={smallInput} placeholder="B.Sc Nursing" value={ed.qualificationName || ''} onChange={e => {
                const next = [...form.educations]; next[i] = { ...ed, qualificationName: e.target.value }; updateSection('educations', next);
              }}/></div>
            <div><label style={lbl}>Level</label>
              <select style={smallInput} value={ed.level || ''} onChange={e => {
                const next = [...form.educations]; next[i] = { ...ed, level: e.target.value }; updateSection('educations', next);
              }}>
                <option value="">—</option>
                <option>Higher Secondary</option><option>Diploma</option><option>Graduation</option>
                <option>Post Graduation</option><option>Certificate</option>
              </select></div>
          </div>
          <div style={row}>
            <div><label style={lbl}>Institute / University</label>
              <input style={smallInput} value={ed.institute || ''} onChange={e => {
                const next = [...form.educations]; next[i] = { ...ed, institute: e.target.value }; updateSection('educations', next);
              }}/></div>
            <div><label style={lbl}>Specialization</label>
              <input style={smallInput} value={ed.specialization || ''} onChange={e => {
                const next = [...form.educations]; next[i] = { ...ed, specialization: e.target.value }; updateSection('educations', next);
              }}/></div>
          </div>
          <div style={row3}>
            <div><label style={lbl}>From</label>
              <input style={smallInput} placeholder="MM/YYYY" value={ed.fromDate || ''} onChange={e => {
                const next = [...form.educations]; next[i] = { ...ed, fromDate: e.target.value }; updateSection('educations', next);
              }}/></div>
            <div><label style={lbl}>To</label>
              <input style={smallInput} placeholder="MM/YYYY" value={ed.toDate || ''} onChange={e => {
                const next = [...form.educations]; next[i] = { ...ed, toDate: e.target.value }; updateSection('educations', next);
              }}/></div>
            <div><label style={lbl}>Grade / Division</label>
              <input style={smallInput} value={ed.grade || ''} onChange={e => {
                const next = [...form.educations]; next[i] = { ...ed, grade: e.target.value }; updateSection('educations', next);
              }}/></div>
          </div>
          <div style={row}>
            <div><label style={lbl}>Course type</label>
              <select style={smallInput} value={ed.courseType || ''} onChange={e => {
                const next = [...form.educations]; next[i] = { ...ed, courseType: e.target.value }; updateSection('educations', next);
              }}>
                <option value="">—</option>
                <option>Full Time</option><option>Part Time</option><option>Correspondence</option>
              </select></div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button onClick={() => { const next = [...form.educations]; next.splice(i, 1); updateSection('educations', next); }}
                style={{ border: `1px solid ${theme.danger}33`, background: theme.danger + '12', color: theme.danger, padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                <Trash2 size={13}/> Remove
              </button>
            </div>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => updateSection('educations', [...(form.educations || []), {}])}
        style={{ ...btnStyle, padding: '6px 12px', fontSize: 12 }}>+ Add qualification</button>
    </>
  );

  const renderWork = () => (
    <>
      <p style={{ fontSize: 13, color: theme.textMuted, marginBottom: 8 }}>Sab previous jobs list karo. Fresher ho to skip karke 'Save & Next' dabao.</p>
      {(form.workHistory || []).map((w, i) => (
        <div key={i} style={{ ...cardStyle, padding: 10, marginBottom: 8, background: theme.sectionMint || '#f0fdf4' }}>
          <div style={row}>
            <div><label style={lbl}>Organization</label>
              <input style={smallInput} value={w.organization || ''} onChange={e => {
                const next = [...form.workHistory]; next[i] = { ...w, organization: e.target.value }; updateSection('workHistory', next);
              }}/></div>
            <div><label style={lbl}>Last designation</label>
              <input style={smallInput} value={w.lastDesignation || ''} onChange={e => {
                const next = [...form.workHistory]; next[i] = { ...w, lastDesignation: e.target.value }; updateSection('workHistory', next);
              }}/></div>
          </div>
          <div style={row3}>
            <div><label style={lbl}>From</label>
              <input style={smallInput} placeholder="DD/MM/YY" value={w.fromDate || ''} onChange={e => {
                const next = [...form.workHistory]; next[i] = { ...w, fromDate: e.target.value }; updateSection('workHistory', next);
              }}/></div>
            <div><label style={lbl}>To</label>
              <input style={smallInput} placeholder="DD/MM/YY" value={w.toDate || ''} onChange={e => {
                const next = [...form.workHistory]; next[i] = { ...w, toDate: e.target.value }; updateSection('workHistory', next);
              }}/></div>
            <div><label style={lbl}>CTC (₹/month)</label>
              <input style={smallInput} value={w.ctcSalary || ''} onChange={e => {
                const next = [...form.workHistory]; next[i] = { ...w, ctcSalary: e.target.value }; updateSection('workHistory', next);
              }}/></div>
          </div>
          <div style={row}>
            <div><label style={lbl}>Area of work</label>
              <input style={smallInput} value={w.areaOfWork || ''} onChange={e => {
                const next = [...form.workHistory]; next[i] = { ...w, areaOfWork: e.target.value }; updateSection('workHistory', next);
              }}/></div>
            <div><label style={lbl}>Location</label>
              <input style={smallInput} value={w.location || ''} onChange={e => {
                const next = [...form.workHistory]; next[i] = { ...w, location: e.target.value }; updateSection('workHistory', next);
              }}/></div>
          </div>
          <div style={{ marginTop: 6, textAlign: 'right' }}>
            <button onClick={() => { const next = [...form.workHistory]; next.splice(i, 1); updateSection('workHistory', next); }}
              style={{ border: `1px solid ${theme.danger}33`, background: theme.danger + '12', color: theme.danger, padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
              <Trash2 size={13}/> Remove
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => updateSection('workHistory', [...(form.workHistory || []), {}])}
        style={{ ...btnStyle, padding: '6px 12px', fontSize: 12 }}>+ Add work experience</button>
    </>
  );

  const renderLanguagesHobbies = () => (
    <>
      <h4 style={{ marginTop: 6, color: theme.navy }}>Languages known</h4>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: theme.sectionCream || '#fffbeb' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>Language</th>
              <th style={{ padding: 8 }}>Read</th><th style={{ padding: 8 }}>Write</th><th style={{ padding: 8 }}>Speak</th>
              <th style={{ width: 30 }}/>
            </tr>
          </thead>
          <tbody>
            {(form.languages || []).map((l, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                <td style={{ padding: 6 }}>
                  <input style={smallInput} value={l.language || ''} onChange={e => {
                    const next = [...form.languages]; next[i] = { ...l, language: e.target.value }; updateSection('languages', next);
                  }}/>
                </td>
                {['read','write','speak'].map(k => (
                  <td key={k} style={{ padding: 6, textAlign: 'center' }}>
                    <input type="checkbox" checked={!!l[k]} onChange={e => {
                      const next = [...form.languages]; next[i] = { ...l, [k]: e.target.checked }; updateSection('languages', next);
                    }}/>
                  </td>
                ))}
                <td>
                  <button onClick={() => { const next = [...form.languages]; next.splice(i,1); updateSection('languages', next); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.danger }}><Trash2 size={13}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={() => updateSection('languages', [...(form.languages || []), { read: true, write: true, speak: true }])}
        style={{ ...btnStyle, padding: '6px 12px', fontSize: 12, marginTop: 8 }}>+ Add language</button>

      <h4 style={{ marginTop: 18, color: theme.navy }}>Hobbies & Interests</h4>
      <textarea style={{ ...inputStyle, minHeight: 72 }} placeholder="Ek line me ek — Badminton, Travelling, Reading…"
        value={(form.hobbies || []).join('\n')}
        onChange={e => updateSection('hobbies', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}/>
    </>
  );

  const renderOtherDetails = () => {
    const ps = form.pursuingStudies || {};
    const bank = form.bankAccount || {};
    const crim = form.criminalProceedings || {};
    const rel = form.relativeEmployed || {};
    const refs = form.references || [];
    return (
      <>
        <h4 style={{ marginTop: 6, color: theme.navy }}>Formal studies in progress?</h4>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <input type="checkbox" checked={!!ps.enrolled} onChange={e => updateSection('pursuingStudies', { ...ps, enrolled: e.target.checked })}/> Yes, currently enrolled
        </label>
        {ps.enrolled && <input style={inputStyle} placeholder="Course name, university, expected year" value={ps.details || ''}
          onChange={e => updateSection('pursuingStudies', { ...ps, details: e.target.value })}/>}

        <h4 style={{ marginTop: 18, color: theme.navy }}>Bank Account (for salary)</h4>
        <div style={row3}>
          <div><label style={lbl}>Bank name</label>
            <input style={smallInput} value={bank.bankName || ''} onChange={e => updateSection('bankAccount', { ...bank, bankName: e.target.value })}/></div>
          <div><label style={lbl}>Account type</label>
            <select style={smallInput} value={bank.accountType || ''} onChange={e => updateSection('bankAccount', { ...bank, accountType: e.target.value })}>
              <option value="">—</option><option>Savings</option><option>Current</option><option>Salary</option>
            </select></div>
          <div><label style={lbl}>Account No</label>
            <input style={smallInput} value={bank.accountNumber || ''} onChange={e => updateSection('bankAccount', { ...bank, accountNumber: e.target.value })}/></div>
        </div>

        <h4 style={{ marginTop: 18, color: theme.navy }}>Criminal proceedings</h4>
        <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={!!crim.any} onChange={e => updateSection('criminalProceedings', { ...crim, any: e.target.checked })}/> Yes, I've been involved in legal proceedings
        </label>
        {crim.any && <textarea style={{ ...inputStyle, minHeight: 60 }} placeholder="Details" value={crim.details || ''}
          onChange={e => updateSection('criminalProceedings', { ...crim, details: e.target.value })}/>}

        <h4 style={{ marginTop: 18, color: theme.navy }}>Is any relative employed with this company?</h4>
        <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={!!rel.any} onChange={e => updateSection('relativeEmployed', { ...rel, any: e.target.checked })}/> Yes
        </label>
        {rel.any && (
          <div style={row3}>
            <input style={smallInput} placeholder="Name" value={rel.name || ''} onChange={e => updateSection('relativeEmployed', { ...rel, name: e.target.value })}/>
            <input style={smallInput} placeholder="Relationship" value={rel.relationship || ''} onChange={e => updateSection('relativeEmployed', { ...rel, relationship: e.target.value })}/>
            <input style={smallInput} placeholder="Designation" value={rel.designation || ''} onChange={e => updateSection('relativeEmployed', { ...rel, designation: e.target.value })}/>
          </div>
        )}

        <h4 style={{ marginTop: 18, color: theme.navy }}>Two personal references</h4>
        <p style={{ fontSize: 12, color: theme.textMuted }}>Responsible people who can be contacted for enquiries.</p>
        {[0,1].map(i => (
          <div key={i} style={row3}>
            <input style={smallInput} placeholder={`Ref ${i+1} name`} value={refs[i]?.name || ''} onChange={e => {
              const next = [...refs]; next[i] = { ...(next[i] || {}), name: e.target.value }; updateSection('references', next);
            }}/>
            <input style={smallInput} placeholder="Designation" value={refs[i]?.designation || ''} onChange={e => {
              const next = [...refs]; next[i] = { ...(next[i] || {}), designation: e.target.value }; updateSection('references', next);
            }}/>
            <input style={smallInput} placeholder="Contact no" value={refs[i]?.contact || ''} onChange={e => {
              const next = [...refs]; next[i] = { ...(next[i] || {}), contact: e.target.value }; updateSection('references', next);
            }}/>
          </div>
        ))}
      </>
    );
  };

  const renderAppointment = () => {
    const a = form.appointmentLetter || {};
    const setA = (f, v) => updateSection('appointmentLetter', { ...a, [f]: v });
    return (
      <>
        <div style={{ ...cardStyle, background: theme.sectionLilac || '#faf5ff', padding: 12, marginBottom: 12, fontSize: 13, color: theme.textMuted }}>
          HR issues an appointment letter with the salary, working hours and weekly-off terms.
          Staff signs below to accept.
        </div>
        <div style={row3}>
          <div><label style={lbl}>Monthly salary (₹)</label>
            <input style={inputStyle} value={a.salaryMonthly || ''} onChange={e => setA('salaryMonthly', e.target.value)}/></div>
          <div><label style={lbl}>Working hours</label>
            <input style={inputStyle} placeholder="10am–5pm" value={a.workingHours || ''} onChange={e => setA('workingHours', e.target.value)}/></div>
          <div><label style={lbl}>Weekly off</label>
            <input style={inputStyle} placeholder="1 day / week" value={a.weeklyOff || ''} onChange={e => setA('weeklyOff', e.target.value)}/></div>
        </div>
        <div style={row}>
          <div><label style={lbl}>Issued by (HR)</label>
            <input style={inputStyle} value={a.issuedBy || ''} onChange={e => setA('issuedBy', e.target.value)}/></div>
          <div><label style={lbl}>Date issued</label>
            <input type="date" style={inputStyle} value={a.issuedDate ? a.issuedDate.slice(0,10) : ''} onChange={e => setA('issuedDate', e.target.value)}/></div>
        </div>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, fontSize: 13 }}>
          <input type="checkbox" checked={!!a.acceptedByEmployee} onChange={e => setA('acceptedByEmployee', e.target.checked)}/>
          I have read and understood the terms and accept the appointment
        </label>
        <div style={row}>
          <div><label style={lbl}>Date accepted</label>
            <input type="date" style={inputStyle} value={a.acceptedDate ? a.acceptedDate.slice(0,10) : ''} onChange={e => setA('acceptedDate', e.target.value)}/></div>
          <div/>
        </div>
        {sigButton('appointmentLetter', 'employeeSignature', 'Employee signature')}
        {sigButton('appointmentLetter', 'hrSignature', 'HR signature')}
      </>
    );
  };

  const renderConfidentiality = () => {
    const c = form.confidentialityAgreement || {};
    const setC = (f, v) => updateSection('confidentialityAgreement', { ...c, [f]: v });
    return (
      <>
        <div style={{ ...cardStyle, padding: 12, marginBottom: 12, fontSize: 12.5, color: theme.textMuted, background: theme.sectionPink || '#fdf2f8', maxHeight: 240, overflowY: 'auto' }}>
          <b>CONFIDENTIAL INFORMATION, INTELLECTUAL PROPERTY RIGHTS AND NON-COMPETE AGREEMENT</b><br/><br/>
          As a condition of my employment with the Hospital: (a) I shall not disclose any confidential information; (b) all IP created during employment vests with the Hospital; (c) I shall not compete or solicit employees for 1 year after separation; (d) all disputes to be governed by the laws of India.
          <br/><br/>By ticking the box below I agree to all the above clauses.
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={!!c.agreed} onChange={e => setC('agreed', e.target.checked)}/>
          I agree to the Confidentiality, IP and Non-Compete Agreement
        </label>
        <div style={row}>
          <div><label style={lbl}>Date signed</label>
            <input type="date" style={inputStyle} value={c.signedDate ? c.signedDate.slice(0,10) : ''} onChange={e => setC('signedDate', e.target.value)}/></div>
          <div><label style={lbl}>Witness name</label>
            <input style={inputStyle} value={c.witnessName || ''} onChange={e => setC('witnessName', e.target.value)}/></div>
        </div>
        {sigButton('confidentialityAgreement', 'employeeSignature', 'Employee signature')}
      </>
    );
  };

  const renderJoining = () => {
    const j = form.joiningInduction || {};
    const setJ = (f, v) => updateSection('joiningInduction', { ...j, [f]: v });
    return (
      <>
        <div style={row}>
          <div><label style={lbl}>Employee code</label>
            <input style={inputStyle} value={j.employeeCode || ''} onChange={e => setJ('employeeCode', e.target.value)}/></div>
          <div><label style={lbl}>Reported to</label>
            <input style={inputStyle} value={j.reportedTo || ''} onChange={e => setJ('reportedTo', e.target.value)}/></div>
        </div>
        <div style={row3}>
          <div><label style={lbl}>Department</label>
            <input style={inputStyle} value={j.department || ''} onChange={e => setJ('department', e.target.value)}/></div>
          <div><label style={lbl}>Induction given at</label>
            <input style={inputStyle} value={j.inductionGivenAt || ''} onChange={e => setJ('inductionGivenAt', e.target.value)}/></div>
          <div><label style={lbl}>Days of induction</label>
            <input style={inputStyle} value={j.daysOfInduction || ''} onChange={e => setJ('daysOfInduction', e.target.value)}/></div>
        </div>
        <div style={row}>
          <div><label style={lbl}>Date</label>
            <input type="date" style={inputStyle} value={j.date ? j.date.slice(0,10) : ''} onChange={e => setJ('date', e.target.value)}/></div>
          <div/>
        </div>
        {sigButton('joiningInduction', 'employeeSignature', 'Employee signature')}
        {sigButton('joiningInduction', 'departmentHeadSignature', 'Department Head signature')}
        {sigButton('joiningInduction', 'hrAuthoritySignature', 'HR authority signature')}
      </>
    );
  };

  const renderAuthLetter = () => {
    const a = form.authorizationLetter || {};
    const setA = (f, v) => updateSection('authorizationLetter', { ...a, [f]: v });
    return (
      <>
        <div style={{ ...cardStyle, padding: 12, marginBottom: 12, fontSize: 12.5, color: theme.textMuted, background: theme.sectionCream || '#fffbeb' }}>
          <b>Letter of Authorization — To Whom It May Concern</b><br/><br/>
          I authorize Siddharth Dental Hospital (or its background-verification agent) to verify and validate the information I have provided, including employment, personal background, professional standing, criminal records and qualifications, from any current/past employer, educational institution, or public/private entity. I release all such parties from liability arising from providing this information.
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={!!a.agreedToBackgroundCheck} onChange={e => setA('agreedToBackgroundCheck', e.target.checked)}/>
          I authorize the background-check as described above
        </label>
        <div style={row}>
          <div><label style={lbl}>Date of birth</label>
            <input type="date" style={inputStyle} value={a.dateOfBirth || ''} onChange={e => setA('dateOfBirth', e.target.value)}/></div>
          <div><label style={lbl}>Date signed</label>
            <input type="date" style={inputStyle} value={a.signedDate ? a.signedDate.slice(0,10) : ''} onChange={e => setA('signedDate', e.target.value)}/></div>
        </div>
        {sigButton('authorizationLetter', 'employeeSignature', 'Employee signature')}
      </>
    );
  };

  const renderBloodConsent = () => {
    const b = form.bloodTestConsent || {};
    const setB = (f, v) => updateSection('bloodTestConsent', { ...b, [f]: v });
    return (
      <>
        <div style={{ ...cardStyle, padding: 12, marginBottom: 12, fontSize: 12.5, color: theme.textMuted, background: theme.sectionMint || '#f0fdf4' }}>
          <b>Consent for Blood Testing</b><br/><br/>
          I hereby give my consent and permit the hospital to test my blood for HIV, HbsAg and HCV as per standardized approved methods. I understand this is for diagnostic purposes only and I have been counseled on the purpose.
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={!!b.consented} onChange={e => setB('consented', e.target.checked)}/>
          I consent to the above blood tests
        </label>
        <div style={row3}>
          <div><label style={lbl}>ID Number</label>
            <input style={inputStyle} value={b.idNumber || ''} onChange={e => setB('idNumber', e.target.value)}/></div>
          <div><label style={lbl}>Place</label>
            <input style={inputStyle} value={b.place || ''} onChange={e => setB('place', e.target.value)}/></div>
          <div><label style={lbl}>Date</label>
            <input type="date" style={inputStyle} value={b.date ? b.date.slice(0,10) : ''} onChange={e => setB('date', e.target.value)}/></div>
        </div>
        {sigButton('bloodTestConsent', 'employeeSignature', 'Employee signature')}
      </>
    );
  };

  const renderHealth = () => {
    const h = form.healthCheckup || {};
    const setH = (f, v) => updateSection('healthCheckup', { ...h, [f]: v });
    return (
      <>
        <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionLilac || '#faf5ff', color: theme.textMuted }}>
          <b>Physician fills this section.</b> Staff may type the history while the doctor examines.
        </div>
        <label style={lbl}>General clinical history</label>
        <textarea style={{ ...inputStyle, minHeight: 50 }} value={h.generalClinicalHistory || ''} onChange={e => setH('generalClinicalHistory', e.target.value)}/>
        <label style={lbl}>Family history</label>
        <textarea style={{ ...inputStyle, minHeight: 50 }} value={h.familyHistory || ''} onChange={e => setH('familyHistory', e.target.value)}/>
        <label style={lbl}>Currently on any medication</label>
        <input style={inputStyle} value={h.currentMedications || ''} onChange={e => setH('currentMedications', e.target.value)}/>
        <div style={row3}>
          <div><label style={lbl}>Height (cm)</label>
            <input style={smallInput} value={h.heightCm || ''} onChange={e => setH('heightCm', e.target.value)}/></div>
          <div><label style={lbl}>Weight (kg)</label>
            <input style={smallInput} value={h.weightKg || ''} onChange={e => setH('weightKg', e.target.value)}/></div>
          <div><label style={lbl}>Pulse (bpm)</label>
            <input style={smallInput} value={h.pulseRate || ''} onChange={e => setH('pulseRate', e.target.value)}/></div>
        </div>
        <div style={row3}>
          <div><label style={lbl}>BP (mmHg)</label>
            <input style={smallInput} placeholder="120/80" value={h.bloodPressure || ''} onChange={e => setH('bloodPressure', e.target.value)}/></div>
          <div><label style={lbl}>Blood Group</label>
            <input style={smallInput} value={h.bloodGroupType || ''} onChange={e => setH('bloodGroupType', e.target.value)}/></div>
          <div><label style={lbl}>Any abnormalities?</label>
            <input style={smallInput} value={h.anyAbnormalities || ''} onChange={e => setH('anyAbnormalities', e.target.value)}/></div>
        </div>
        <div style={row}>
          <div><label style={lbl}>Chest X-ray</label>
            <select style={inputStyle} value={h.xRayChest || ''} onChange={e => setH('xRayChest', e.target.value)}>
              <option value="">—</option><option value="normal">Normal</option><option value="abnormal">Abnormal</option>
            </select></div>
          <div><label style={lbl}>CBC</label>
            <select style={inputStyle} value={h.cbc || ''} onChange={e => setH('cbc', e.target.value)}>
              <option value="">—</option><option value="normal">Normal</option><option value="abnormal">Abnormal</option>
            </select></div>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: 13 }}>
          <input type="checkbox" checked={!!h.medicallyFit} onChange={e => setH('medicallyFit', e.target.checked)}/>
          Employee is medically fit to join duty
        </label>
        <label style={lbl}>Comments</label>
        <textarea style={{ ...inputStyle, minHeight: 40 }} value={h.comments || ''} onChange={e => setH('comments', e.target.value)}/>
        <div style={row}>
          <div><label style={lbl}>Physician name</label>
            <input style={inputStyle} value={h.physicianName || ''} onChange={e => setH('physicianName', e.target.value)}/></div>
          <div><label style={lbl}>Date</label>
            <input type="date" style={inputStyle} value={h.date ? h.date.slice(0,10) : ''} onChange={e => setH('date', e.target.value)}/></div>
        </div>
        {sigButton('healthCheckup', 'physicianSignature', 'Physician signature')}
        {sigButton('healthCheckup', 'employeeSignature', 'Employee signature')}

        <div style={{ ...cardStyle, padding: 10, marginTop: 12, background: theme.sectionBlue || '#f0f9ff', fontSize: 12.5 }}>
          CBC / X-ray ki lab report upload karne ke liye <b>Attached Reports</b> tab pe jao.
        </div>
      </>
    );
  };

  const renderMedCert = () => {
    const m = form.medicalCertificate || {};
    const setM = (f, v) => updateSection('medicalCertificate', { ...m, [f]: v });
    return (
      <>
        <label style={lbl}>Medical verdict</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {[['fit','Fit',theme.success],['unfit','Unfit',theme.danger],['temporarily_unfit','Temporarily Unfit',theme.warning]].map(([val,l,c]) => (
            <button key={val} type="button" onClick={() => setM('verdict', val)} style={chipBtn(m.verdict === val, c)}>{l}</button>
          ))}
        </div>
        <label style={lbl}>Findings / notes</label>
        <textarea style={{ ...inputStyle, minHeight: 50 }} value={m.findings || ''} onChange={e => setM('findings', e.target.value)}/>
        <div style={row3}>
          <div><label style={lbl}>Doctor name</label>
            <input style={smallInput} value={m.doctorName || ''} onChange={e => setM('doctorName', e.target.value)}/></div>
          <div><label style={lbl}>Registration No</label>
            <input style={smallInput} value={m.registrationNo || ''} onChange={e => setM('registrationNo', e.target.value)}/></div>
          <div><label style={lbl}>Date</label>
            <input type="date" style={smallInput} value={m.date ? m.date.slice(0,10) : ''} onChange={e => setM('date', e.target.value)}/></div>
        </div>
        {sigButton('medicalCertificate', 'examinerSignature', 'Examining doctor signature')}
        {sigButton('medicalCertificate', 'chiefMedicalOfficerSignature', 'Chief medical officer signature')}
      </>
    );
  };

  const renderHepatitis = () => {
    const h = form.hepatitisBDeclaration || {};
    const setH = (f, v) => updateSection('hepatitisBDeclaration', { ...h, [f]: v });
    return (
      <>
        <div style={{ ...cardStyle, padding: 10, marginBottom: 12, fontSize: 12.5, background: theme.sectionMint || '#f0fdf4', color: theme.textMuted }}>
          <b>Hepatitis-B Vaccine Declaration</b> — tick whichever applies.
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={!!h.vaccinated} onChange={e => setH('vaccinated', e.target.checked)}/>
          I have been vaccinated — full course of 3 doses completed
        </label>
        {h.vaccinated && (
          <div><label style={lbl}>Date of completion</label>
            <input style={inputStyle} placeholder="DD/MM/YYYY" value={h.dosesCompletedOn || ''} onChange={e => setH('dosesCompletedOn', e.target.value)}/></div>
        )}
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 10 }}>
          <input type="checkbox" checked={!!h.consentsToVaccinate} onChange={e => setH('consentsToVaccinate', e.target.checked)}/>
          I have not been vaccinated — I consent to receive HBV vaccine
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 6 }}>
          <input type="checkbox" checked={!!h.acknowledgesRisk} onChange={e => setH('acknowledgesRisk', e.target.checked)}/>
          I understand occupational exposure risks and accept them
        </label>
        <div style={row}>
          <div><label style={lbl}>Place</label><input style={inputStyle} value={h.place || ''} onChange={e => setH('place', e.target.value)}/></div>
          <div><label style={lbl}>Date</label><input type="date" style={inputStyle} value={h.date ? h.date.slice(0,10) : ''} onChange={e => setH('date', e.target.value)}/></div>
        </div>
        {sigButton('hepatitisBDeclaration', 'employeeSignature', 'Employee signature')}
      </>
    );
  };

  const renderCriminalAffidavit = () => {
    const a = form.noCriminalAffidavit || {};
    const setA = (f, v) => updateSection('noCriminalAffidavit', { ...a, [f]: v });
    return (
      <>
        <div style={{ ...cardStyle, padding: 12, marginBottom: 12, fontSize: 12.5, background: theme.sectionPink || '#fdf2f8', color: theme.textMuted }}>
          <b>No Criminal Offense Affidavit</b><br/><br/>
          I solemnly affirm and declare that no criminal offence is registered or pending against me in any court of law, and I wish to join the Hospital.
        </div>
        <div style={row3}>
          <div><label style={lbl}>Father's name</label><input style={inputStyle} value={a.fatherName || ''} onChange={e => setA('fatherName', e.target.value)}/></div>
          <div><label style={lbl}>Nationality</label><input style={inputStyle} value={a.nationality || 'Indian'} onChange={e => setA('nationality', e.target.value)}/></div>
          <div><label style={lbl}>Religion</label><input style={inputStyle} value={a.religion || ''} onChange={e => setA('religion', e.target.value)}/></div>
        </div>
        <label style={lbl}>Residing at (full address)</label>
        <textarea style={{ ...inputStyle, minHeight: 50 }} value={a.residence || ''} onChange={e => setA('residence', e.target.value)}/>
        <div style={row}>
          <div><label style={lbl}>Years at residence</label><input style={inputStyle} value={a.yearsAtResidence || ''} onChange={e => setA('yearsAtResidence', e.target.value)}/></div>
          <div><label style={lbl}>Date</label><input type="date" style={inputStyle} value={a.date ? a.date.slice(0,10) : ''} onChange={e => setA('date', e.target.value)}/></div>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 10 }}>
          <input type="checkbox" checked={!!a.affirmsNoCriminal} onChange={e => setA('affirmsNoCriminal', e.target.checked)}/>
          I solemnly affirm no criminal offense registered / pending against me
        </label>
        {sigButton('noCriminalAffidavit', 'employeeSignature', 'Deponent signature')}
      </>
    );
  };

  const renderEmployerRef = () => {
    const r = form.employerReferenceCheck || {};
    const setR = (f, v) => updateSection('employerReferenceCheck', { ...r, [f]: v });
    return (
      <>
        <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionCream || '#fffbeb', color: theme.textMuted }}>
          <b>Reference Check — HR fills this from the ex-employer's records.</b>
        </div>
        <div style={row}>
          <div><label style={lbl}>Ex-employer name</label><input style={inputStyle} value={r.exEmployerName || ''} onChange={e => setR('exEmployerName', e.target.value)}/></div>
          <div><label style={lbl}>Contact (phone / email)</label><input style={inputStyle} value={r.exEmployerContact || ''} onChange={e => setR('exEmployerContact', e.target.value)}/></div>
        </div>
        <label style={lbl}>Address</label>
        <textarea style={{ ...inputStyle, minHeight: 40 }} value={r.exEmployerAddress || ''} onChange={e => setR('exEmployerAddress', e.target.value)}/>
        <div style={row}>
          <div><label style={lbl}>Designation held</label><input style={inputStyle} value={r.designationHeld || ''} onChange={e => setR('designationHeld', e.target.value)}/></div>
          <div><label style={lbl}>Department</label><input style={inputStyle} value={r.department || ''} onChange={e => setR('department', e.target.value)}/></div>
        </div>
        <div style={row}>
          <div><label style={lbl}>Reporting authority</label><input style={inputStyle} value={r.reportingAuthority || ''} onChange={e => setR('reportingAuthority', e.target.value)}/></div>
          <div><label style={lbl}>Employment period</label><input style={inputStyle} placeholder="3-4-22 to 31-10-22" value={r.employmentPeriod || ''} onChange={e => setR('employmentPeriod', e.target.value)}/></div>
        </div>
        {sigButton('employerReferenceCheck', 'employeeSignature', 'Employee declaration signature')}
      </>
    );
  };

  const renderTelephonicRef = () => {
    const t = form.telephonicReferenceCheck || {};
    const setT = (f, v) => updateSection('telephonicReferenceCheck', { ...t, [f]: v });
    return (
      <>
        <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionBlue || '#f0f9ff', color: theme.textMuted }}>
          <b>Telephonic Reference Check</b> — HR calls the reference and fills this.
        </div>
        <div style={row}>
          <div><label style={lbl}>Person spoken to</label><input style={inputStyle} value={t.personSpoken || ''} onChange={e => setT('personSpoken', e.target.value)}/></div>
          <div><label style={lbl}>Designation</label><input style={inputStyle} value={t.designation || ''} onChange={e => setT('designation', e.target.value)}/></div>
        </div>
        <div style={row3}>
          <div><label style={lbl}>Mobile</label><input style={inputStyle} value={t.mobileNo || ''} onChange={e => setT('mobileNo', e.target.value)}/></div>
          <div><label style={lbl}>Employment period</label><input style={inputStyle} value={t.employmentPeriod || ''} onChange={e => setT('employmentPeriod', e.target.value)}/></div>
          <div><label style={lbl}>Position held</label><input style={inputStyle} value={t.positionHeld || ''} onChange={e => setT('positionHeld', e.target.value)}/></div>
        </div>
        <label style={lbl}>Reason for leaving</label>
        <input style={inputStyle} value={t.reasonForLeaving || ''} onChange={e => setT('reasonForLeaving', e.target.value)}/>
        <label style={lbl}>Pending obligations?</label>
        <input style={inputStyle} value={t.pendingObligation || ''} onChange={e => setT('pendingObligation', e.target.value)}/>
        {[
          ['knowledgeSkill','Knowledge & skill for job'],
          ['abilityToWorkWithOthers','Ability to work with others'],
          ['dependability','Dependability'],
          ['criminalBackground','Criminal / negligence background'],
          ['professionalConduct','Professional conduct'],
          ['moralConduct','Moral conduct'],
          ['health','Health'],
          ['appearance','Appearance & grooming'],
        ].map(([k,l]) => (
          <div key={k}><label style={lbl}>{l}</label>
            <input style={inputStyle} value={t[k] || ''} onChange={e => setT(k, e.target.value)}/></div>
        ))}
        <label style={lbl}>Would you re-employ?</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {['yes','no'].map(v => <button key={v} type="button" onClick={() => setT('wouldReEmploy', v)} style={chipBtn(t.wouldReEmploy === v, v === 'yes' ? theme.success : theme.danger)}>{v.toUpperCase()}</button>)}
        </div>
        {t.wouldReEmploy === 'no' && (
          <>
            <label style={lbl}>Please explain</label>
            <textarea style={{ ...inputStyle, minHeight: 40 }} value={t.reEmployExplanation || ''} onChange={e => setT('reEmployExplanation', e.target.value)}/>
          </>
        )}
        <div style={row}>
          <div><label style={lbl}>HR representative</label><input style={inputStyle} value={t.hrRepresentative || ''} onChange={e => setT('hrRepresentative', e.target.value)}/></div>
          <div><label style={lbl}>Date</label><input type="date" style={inputStyle} value={t.date ? t.date.slice(0,10) : ''} onChange={e => setT('date', e.target.value)}/></div>
        </div>
        {sigButton('telephonicReferenceCheck', 'hrSignature', 'HR signature')}
      </>
    );
  };

  const renderCredentials = () => {
    const c = form.credentialsVerification || {};
    const setC = (f, v) => updateSection('credentialsVerification', { ...c, [f]: v });
    const DOCS = [
      ['aadharCard','Aadhaar Card'],
      ['panCard','PAN Card'],
      ['qualificationDocs','Qualification documents'],
      ['policeVerification','Police verification'],
      ['experienceCertificate','Experience certificate'],
      ['relievingLetter','Relieving letter'],
      ['salarySlip','Salary slip'],
      ['passportPhoto','Passport-size photo'],
    ];
    return (
      <>
        <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionMint || '#f0fdf4', color: theme.textMuted }}>
          Staff ticks "Submitted", HR ticks "Verified" after physical check.
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.sectionCream || '#fffbeb' }}>
                <th style={{ padding: 8, textAlign: 'left' }}>Document</th>
                <th style={{ padding: 8 }}>Submitted by staff</th>
                <th style={{ padding: 8 }}>Verified by HR</th>
              </tr>
            </thead>
            <tbody>
              {DOCS.map(([k,l]) => (
                <tr key={k} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                  <td style={{ padding: 8 }}>{l}</td>
                  <td style={{ padding: 8, textAlign: 'center' }}>
                    <input type="checkbox" checked={!!c[k]?.submitted} onChange={e => setC(k, { ...(c[k] || {}), submitted: e.target.checked })}/>
                  </td>
                  <td style={{ padding: 8, textAlign: 'center' }}>
                    <input type="checkbox" checked={!!c[k]?.verified} onChange={e => setC(k, { ...(c[k] || {}), verified: e.target.checked })}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={row}>
          <div><label style={lbl}>Employee date</label>
            <input type="date" style={inputStyle} value={c.employeeDate ? c.employeeDate.slice(0,10) : ''} onChange={e => setC('employeeDate', e.target.value)}/></div>
          <div><label style={lbl}>HR date</label>
            <input type="date" style={inputStyle} value={c.hrDate ? c.hrDate.slice(0,10) : ''} onChange={e => setC('hrDate', e.target.value)}/></div>
        </div>
        {sigButton('credentialsVerification', 'employeeSignature', 'Employee signature')}
        {sigButton('credentialsVerification', 'hrSignature', 'HR signature')}
      </>
    );
  };

  const renderAttachments = () => {
    const list = form.attachments || [];
    const KINDS = [
      { v: 'photo',  l: 'Profile photo'         },
      { v: 'aadhar', l: 'Aadhaar card'          },
      { v: 'pan',    l: 'PAN card'              },
      { v: 'cert',   l: 'Qualification certs'   },
      { v: 'cbc',    l: 'CBC / Hematology report' },
      { v: 'xray',   l: 'Chest X-ray report'    },
      { v: 'exp',    l: 'Experience / relieving letter' },
      { v: 'other',  l: 'Other'                 },
    ];
    return (
      <>
        <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionBlue || '#f0f9ff', color: theme.textMuted }}>
          Lab reports (CBC, X-ray) aur koi bhi scanned document yahan upload karo. Photos (JPG/PNG) ya PDF chalega. 25 MB tak ki file.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
          {KINDS.map(k => (
            <label key={k.v} style={{ ...cardStyle, padding: 10, textAlign: 'center', cursor: 'pointer', border: `1px dashed ${theme.primary}`, background: 'white' }}>
              <Upload size={20} color={theme.primary}/>
              <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, color: theme.navy }}>{k.l}</div>
              <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => handleFile(e, k.v)} disabled={busyUpload}/>
            </label>
          ))}
        </div>
        {busyUpload && <div style={{ color: theme.info, fontSize: 13 }}>Uploading…</div>}

        {list.length === 0 ? (
          <div style={{ textAlign: 'center', color: theme.textSubtle, padding: 20, fontSize: 13 }}>No attachments yet</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {list.map((a, i) => (
              <div key={i} style={{ ...cardStyle, padding: 8, position: 'relative' }}>
                {a.fileMime?.startsWith('image/')
                  ? <img src={a.url + '?t=' + (sessionStorage.getItem('token') || localStorage.getItem('token') || '')} alt={a.label} style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 4 }}/>
                  : <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme.sectionCream || '#fffbeb', borderRadius: 4 }}><FileText size={28} color={theme.textMuted}/></div>}
                <div style={{ fontSize: 11.5, fontWeight: 600, color: theme.navy, marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.label}>{a.label}</div>
                <div style={{ fontSize: 10, color: theme.textSubtle }}>{a.kind}</div>
                <button onClick={() => removeAttachment(i)} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(255,255,255,0.9)', border: `1px solid ${theme.danger}`, borderRadius: 4, padding: 2, cursor: 'pointer', color: theme.danger }}>
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  // ─── §17 JOB DESCRIPTION ───
  const renderJobDescription = () => {
    const jd = form.jobDescription || {};
    const setJd = (f, v) => updateSection('jobDescription', { ...jd, [f]: v });
    const blocks = jd.responsibilityBlocks || [];
    const setBlocks = (b) => setJd('responsibilityBlocks', b);
    return (
      <>
        <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionLilac || '#faf5ff', color: theme.textMuted }}>
          <b>Job Description</b> — HR role ke liye standard JD upload kare. Staff "I have read" tick karke sign kare.
        </div>
        <div style={row}>
          <div><label style={lbl}>Job title</label>
            <input style={inputStyle} value={jd.jobTitle || ''} onChange={e => setJd('jobTitle', e.target.value)}/></div>
          <div><label style={lbl}>Acknowledged date</label>
            <input type="date" style={inputStyle} value={jd.date ? jd.date.slice(0,10) : ''} onChange={e => setJd('date', e.target.value)}/></div>
        </div>
        <label style={lbl}>Job summary</label>
        <textarea style={{ ...inputStyle, minHeight: 70 }} value={jd.jobSummary || ''} onChange={e => setJd('jobSummary', e.target.value)}/>

        <h4 style={{ marginTop: 16, color: theme.navy }}>Key Responsibilities</h4>
        {blocks.map((b, i) => (
          <div key={i} style={{ ...cardStyle, padding: 10, marginBottom: 8, background: theme.sectionCream || '#fffbeb' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input style={{ ...inputStyle, fontWeight: 700 }} placeholder={`Block ${i + 1} title (e.g. Recruitment & Staffing)`}
                value={b.title || ''} onChange={e => { const n = [...blocks]; n[i] = { ...b, title: e.target.value }; setBlocks(n); }}/>
              <button onClick={() => { const n = [...blocks]; n.splice(i,1); setBlocks(n); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.danger }}>
                <Trash2 size={15}/>
              </button>
            </div>
            <textarea style={{ ...inputStyle, minHeight: 70, marginTop: 6 }} placeholder="Ek bullet per line"
              value={(b.bullets || []).join('\n')}
              onChange={e => { const n = [...blocks]; n[i] = { ...b, bullets: e.target.value.split('\n') }; setBlocks(n); }}/>
          </div>
        ))}
        <button type="button" onClick={() => setBlocks([...blocks, { title: '', bullets: [] }])}
          style={{ ...btnStyle, padding: '6px 12px', fontSize: 12, marginTop: 4 }}>+ Add responsibility block</button>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 16, fontSize: 13 }}>
          <input type="checkbox" checked={!!jd.employeeAcknowledged} onChange={e => setJd('employeeAcknowledged', e.target.checked)}/>
          I have read and understood my job description
        </label>
        {sigButton('jobDescription', 'employeeSignature', 'Employee signature')}
        {sigButton('jobDescription', 'hrSignature', 'HR signature')}
      </>
    );
  };

  // ─── §18 TRAINING LOG ───
  const renderTrainingLog = () => {
    const tl = form.trainingLog || {};
    const seedTopics = [
      'Scope of Services','Grievance Protocol','Fire and Non Fire Emergency','Disciplinary Action',
      'Needle Stick Injury','Bio Medical Waste Segregation','Medication Error','Performance Appraisal',
      'Patient Rights and Education','Infection Control Practices','Spill Management','Disciplinary Procedures',
      'Safety Education Programme','Child Abduction','Cardio Pulmonary Resuscitation','Employee Rights and Education',
    ];
    const entries = tl.entries && tl.entries.length > 0
      ? tl.entries
      : seedTopics.map(topic => ({ topic, dateOfTraining: '', result: '', status: '', retrainingRequired: '', hrSignature: '' }));
    const setE = (next) => updateSection('trainingLog', { ...tl, entries: next });
    return (
      <>
        <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionBlue || '#f0f9ff', color: theme.textMuted }}>
          <b>Training Log</b> — har mandatory training ke baad date + result + retraining flag fill karo.
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse', minWidth: 780 }}>
            <thead>
              <tr style={{ background: theme.sectionCream || '#fffbeb' }}>
                <th style={{ padding: 6, width: 30 }}>#</th>
                <th style={{ padding: 6, textAlign: 'left' }}>Topic</th>
                <th style={{ padding: 6 }}>Date</th>
                <th style={{ padding: 6 }}>Result</th>
                <th style={{ padding: 6 }}>Status</th>
                <th style={{ padding: 6 }}>Retrain?</th>
                <th style={{ width: 28 }}/>
              </tr>
            </thead>
            <tbody>
              {entries.map((en, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                  <td style={{ padding: 4, textAlign: 'center', fontWeight: 700, color: theme.textMuted }}>{i + 1}</td>
                  <td style={{ padding: 4 }}>
                    <input style={smallInput} value={en.topic || ''} onChange={e => { const n = [...entries]; n[i] = { ...en, topic: e.target.value }; setE(n); }}/>
                  </td>
                  <td style={{ padding: 4 }}>
                    <input style={{ ...smallInput, width: 100 }} placeholder="dd/mm/yy" value={en.dateOfTraining || ''} onChange={e => { const n = [...entries]; n[i] = { ...en, dateOfTraining: e.target.value }; setE(n); }}/>
                  </td>
                  <td style={{ padding: 4 }}>
                    <select style={{ ...smallInput, width: 90 }} value={en.result || ''} onChange={e => { const n = [...entries]; n[i] = { ...en, result: e.target.value }; setE(n); }}>
                      <option value="">—</option><option>PASS</option><option>FAIL</option><option>NA</option>
                    </select>
                  </td>
                  <td style={{ padding: 4 }}>
                    <input style={{ ...smallInput, width: 100 }} value={en.status || ''} onChange={e => { const n = [...entries]; n[i] = { ...en, status: e.target.value }; setE(n); }}/>
                  </td>
                  <td style={{ padding: 4, textAlign: 'center' }}>
                    <select style={{ ...smallInput, width: 70 }} value={en.retrainingRequired || ''} onChange={e => { const n = [...entries]; n[i] = { ...en, retrainingRequired: e.target.value }; setE(n); }}>
                      <option value="">—</option><option value="Y">Y</option><option value="N">N</option>
                    </select>
                  </td>
                  <td>
                    <button onClick={() => { const n = [...entries]; n.splice(i,1); setE(n); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.danger }}><Trash2 size={13}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={() => setE([...entries, { topic: '', dateOfTraining: '', result: '', status: '', retrainingRequired: '', hrSignature: '' }])}
          style={{ ...btnStyle, padding: '6px 12px', fontSize: 12, marginTop: 8 }}>+ Add training topic</button>
      </>
    );
  };

  // ─── §19 APPRAISAL LETTER ───
  const renderAppraisalLetter = () => {
    const al = form.appraisalLetter || {};
    const prev = al.previous || {};
    const rev  = al.revised  || {};
    const setA = (f, v) => updateSection('appraisalLetter', { ...al, [f]: v });
    const setP = (f, v) => updateSection('appraisalLetter', { ...al, previous: { ...prev, [f]: v } });
    const setR = (f, v) => updateSection('appraisalLetter', { ...al, revised:  { ...rev,  [f]: v } });
    return (
      <>
        <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionPink || '#fdf2f8', color: theme.textMuted }}>
          <b>Annual Appraisal Letter</b> — salary / designation / leaves revision. Director + employee sign karte hain.
        </div>
        <div style={row}>
          <div><label style={lbl}>Appraisal year</label>
            <input style={inputStyle} placeholder="2023-24" value={al.appraisalYear || ''} onChange={e => setA('appraisalYear', e.target.value)}/></div>
          <div><label style={lbl}>Effective from</label>
            <input type="date" style={inputStyle} value={al.effectiveFromDate ? String(al.effectiveFromDate).slice(0,10) : ''} onChange={e => setA('effectiveFromDate', e.target.value)}/></div>
        </div>
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.sectionCream || '#fffbeb' }}>
                <th style={{ padding: 8, textAlign: 'left' }}>Particular</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Previous</th>
                <th style={{ padding: 8, textAlign: 'left' }}>After Revision</th>
              </tr>
            </thead>
            <tbody>
              {[['designation','Designation'],['salary','Salary (₹/month)'],['leaves','Leaves'],['otherBenefits','Other benefits']].map(([k,l]) => (
                <tr key={k} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                  <td style={{ padding: 6, fontWeight: 600, color: theme.navy }}>{l}</td>
                  <td style={{ padding: 6 }}>
                    <input style={smallInput} value={prev[k] || ''} onChange={e => setP(k, e.target.value)}/>
                  </td>
                  <td style={{ padding: 6 }}>
                    <input style={smallInput} value={rev[k] || ''} onChange={e => setR(k, e.target.value)}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <label style={lbl}>Remarks (if any)</label>
        <textarea style={{ ...inputStyle, minHeight: 50 }} value={al.remarks || ''} onChange={e => setA('remarks', e.target.value)}/>
        {sigButton('appraisalLetter', 'employeeSignature', 'Employee signature')}
        {sigButton('appraisalLetter', 'directorSignature', 'Director / Admin signature')}
      </>
    );
  };

  // ─── §20 PERFORMANCE APPRAISAL ───
  const renderPerformanceAppraisal = () => {
    const pa = form.performanceAppraisal || {};
    const period = pa.appraisalPeriod || {};
    const setA = (f, v) => updateSection('performanceAppraisal', { ...pa, [f]: v });
    const setP = (f, v) => updateSection('performanceAppraisal', { ...pa, appraisalPeriod: { ...period, [f]: v } });

    const setRating = (groupKey, itemKey, who, val) => {
      const group = pa[groupKey] || {};
      const entry = group[itemKey] || { self: 0, reporting: 0, reviewing: 0 };
      const clamped = Math.max(0, Math.min(5, Number(val) || 0));
      const next = { ...group, [itemKey]: { ...entry, [who]: clamped } };
      updateSection('performanceAppraisal', { ...pa, [groupKey]: next });
    };
    const getVal = (groupKey, itemKey, who) => {
      return pa[groupKey]?.[itemKey]?.[who] ?? '';
    };
    const sumFor = (who) => {
      const sum = (group, items) => (items || []).reduce((acc, [k]) => acc + Number(pa[group]?.[k]?.[who] || 0), 0);
      return sum('personalTraits', PA_PERSONAL) + sum('organizationalAlignment', PA_ORG) + sum('professionalAbilities', PA_PRO);
    };
    const gradeFor = (total) => PA_GRADES.find(g => total >= g.min && total <= g.max) || PA_GRADES[PA_GRADES.length - 1];

    const renderRatingBlock = (groupKey, items, heading) => (
      <div style={{ marginTop: 12 }}>
        <h4 style={{ color: theme.navy, marginBottom: 6 }}>{heading} <span style={{ fontSize: 11, color: theme.textMuted, fontWeight: 400 }}>(each /5)</span></h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr style={{ background: theme.sectionCream || '#fffbeb' }}>
                <th style={{ padding: 6, textAlign: 'left' }}>Trait</th>
                <th style={{ padding: 6, textAlign: 'left' }}>Description</th>
                <th style={{ padding: 6 }}>Self</th>
                <th style={{ padding: 6 }}>Reporting</th>
                <th style={{ padding: 6 }}>Reviewing</th>
              </tr>
            </thead>
            <tbody>
              {items.map(([k, lab, desc]) => (
                <tr key={k} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                  <td style={{ padding: 6, fontWeight: 600, color: theme.navy, whiteSpace: 'nowrap' }}>{lab}</td>
                  <td style={{ padding: 6, fontSize: 11.5, color: theme.textMuted }}>{desc}</td>
                  {['self','reporting','reviewing'].map(who => (
                    <td key={who} style={{ padding: 4, textAlign: 'center' }}>
                      <input type="number" min={0} max={5} style={{ ...smallInput, width: 56, textAlign: 'center' }}
                        value={getVal(groupKey, k, who)}
                        onChange={e => setRating(groupKey, k, who, e.target.value)}/>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );

    const selfTot   = sumFor('self');
    const reportTot = sumFor('reporting');
    const revTot    = sumFor('reviewing');
    const finalGr   = gradeFor(revTot || reportTot || selfTot);

    const goals = pa.goals || [];
    const setGoals = (n) => updateSection('performanceAppraisal', { ...pa, goals: n });

    return (
      <>
        <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionLilac || '#faf5ff', color: theme.textMuted }}>
          <b>Performance Appraisal Form</b> — 100 marks ka evaluation. Appraisee self-rates, Reporting & Reviewing Officer confirm.
        </div>
        <div style={row3}>
          <div><label style={lbl}>Period from</label>
            <input style={inputStyle} placeholder="1 Apr 2023" value={period.from || ''} onChange={e => setP('from', e.target.value)}/></div>
          <div><label style={lbl}>Period to</label>
            <input style={inputStyle} placeholder="31 Mar 2024" value={period.to || ''} onChange={e => setP('to', e.target.value)}/></div>
          <div><label style={lbl}>Date of prev. appraisal</label>
            <input style={inputStyle} value={pa.dateOfPrevAppraisal || ''} onChange={e => setA('dateOfPrevAppraisal', e.target.value)}/></div>
        </div>
        <div style={row3}>
          <div><label style={lbl}>Present salary (₹)</label>
            <input style={inputStyle} value={pa.presentSalary || ''} onChange={e => setA('presentSalary', e.target.value)}/></div>
          <div><label style={lbl}>Last increment</label>
            <input style={inputStyle} value={pa.lastIncrement || ''} onChange={e => setA('lastIncrement', e.target.value)}/></div>
          <div><label style={lbl}># Increments so far</label>
            <input style={inputStyle} value={pa.numIncrements || ''} onChange={e => setA('numIncrements', e.target.value)}/></div>
        </div>
        <div style={row}>
          <div><label style={lbl}>Any memos / warnings</label>
            <input style={inputStyle} value={pa.anyMemos || ''} onChange={e => setA('anyMemos', e.target.value)}/></div>
          <div><label style={lbl}>Any appreciation / award</label>
            <input style={inputStyle} value={pa.anyAppreciation || ''} onChange={e => setA('anyAppreciation', e.target.value)}/></div>
        </div>

        {renderRatingBlock('personalTraits',          PA_PERSONAL, 'Personal Traits')}
        {renderRatingBlock('organizationalAlignment', PA_ORG,      'Organizational Alignment')}
        {renderRatingBlock('professionalAbilities',   PA_PRO,      'Professional Abilities')}

        <div style={{ marginTop: 14, ...cardStyle, padding: 12, background: finalGr.color + '14' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
            <div><b>Self:</b> {selfTot} / 150</div>
            <div><b>Reporting:</b> {reportTot} / 150</div>
            <div><b>Reviewing:</b> {revTot} / 150</div>
            <div style={{ marginLeft: 'auto', fontWeight: 700, color: finalGr.color }}>
              Grade <b>{finalGr.grade}</b> — {finalGr.label}
            </div>
          </div>
        </div>

        <label style={lbl}>Training needs (appraisee fills — what do you want to learn?)</label>
        <textarea style={{ ...inputStyle, minHeight: 60 }} value={pa.trainingNeeds || ''} onChange={e => setA('trainingNeeds', e.target.value)}/>

        <label style={lbl}>Reporting Officer remarks</label>
        <textarea style={{ ...inputStyle, minHeight: 50 }} value={pa.reportingOfficerRemarks || ''} onChange={e => setA('reportingOfficerRemarks', e.target.value)}/>
        <label style={lbl}>Reviewing Officer remarks</label>
        <textarea style={{ ...inputStyle, minHeight: 50 }} value={pa.reviewingOfficerRemarks || ''} onChange={e => setA('reviewingOfficerRemarks', e.target.value)}/>

        <h4 style={{ marginTop: 16, color: theme.navy }}>Goal Setting (SMART)</h4>
        {goals.map((g, i) => (
          <div key={i} style={row3}>
            <input style={smallInput} placeholder="Goal" value={g.goal || ''} onChange={e => { const n = [...goals]; n[i] = { ...g, goal: e.target.value }; setGoals(n); }}/>
            <input style={smallInput} placeholder="Target date" value={g.targetDate || ''} onChange={e => { const n = [...goals]; n[i] = { ...g, targetDate: e.target.value }; setGoals(n); }}/>
            <div style={{ display: 'flex', gap: 6 }}>
              <input style={smallInput} placeholder="Status" value={g.statusOnAppraisal || ''} onChange={e => { const n = [...goals]; n[i] = { ...g, statusOnAppraisal: e.target.value }; setGoals(n); }}/>
              <button onClick={() => { const n = [...goals]; n.splice(i,1); setGoals(n); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.danger }}><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setGoals([...goals, { goal: '', targetDate: '', statusOnAppraisal: '' }])}
          style={{ ...btnStyle, padding: '6px 12px', fontSize: 12, marginTop: 4 }}>+ Add goal</button>

        <h4 style={{ marginTop: 16, color: theme.navy }}>Increment Decision</h4>
        <div style={row3}>
          <div><label style={lbl}>Increment suggested?</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['yes','no'].map(v => (
                <button key={v} type="button" onClick={() => setA('incrementSuggested', v)}
                  style={chipBtn(pa.incrementSuggested === v, v === 'yes' ? theme.success : theme.danger)}>{v.toUpperCase()}</button>
              ))}
            </div>
          </div>
          <div><label style={lbl}>Increment approved (₹)</label>
            <input style={inputStyle} value={pa.incrementApprovedRs || ''} onChange={e => setA('incrementApprovedRs', e.target.value)}/></div>
          <div><label style={lbl}>MD final approval notes</label>
            <input style={inputStyle} value={pa.mdFinalApproval || ''} onChange={e => setA('mdFinalApproval', e.target.value)}/></div>
        </div>

        <label style={lbl}>Action taken by HR</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[['training','Training & Development'],['transfer','Transfer / Job Rotation'],['appreciation','Letter of Appreciation'],['counseling','Counseling Oral/Written']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setA('actionTakenByHr', v)}
              style={chipBtn(pa.actionTakenByHr === v, theme.primary)}>{l}</button>
          ))}
        </div>

        <div style={{ marginTop: 14 }}>
          {sigButton('performanceAppraisal', 'appraiseeSignature',         'Appraisee signature')}
          {sigButton('performanceAppraisal', 'reportingOfficerSignature', 'Reporting Officer signature')}
          {sigButton('performanceAppraisal', 'reviewingOfficerSignature', 'Reviewing Officer signature')}
          {sigButton('performanceAppraisal', 'mdSignature',               'Managing Director signature')}
        </div>
      </>
    );
  };

  const SECTION_RENDER = {
    profile: renderProfile,
    addresses: renderAddresses,
    educations: renderEducations,
    workHistory: renderWork,
    languages: renderLanguagesHobbies,
    otherDetails: renderOtherDetails,
    appointmentLetter: renderAppointment,
    confidentialityAgreement: renderConfidentiality,
    joiningInduction: renderJoining,
    authorizationLetter: renderAuthLetter,
    bloodTestConsent: renderBloodConsent,
    healthCheckup: renderHealth,
    medicalCertificate: renderMedCert,
    hepatitisBDeclaration: renderHepatitis,
    noCriminalAffidavit: renderCriminalAffidavit,
    employerReferenceCheck: renderEmployerRef,
    telephonicReferenceCheck: renderTelephonicRef,
    credentialsVerification: renderCredentials,
    jobDescription: renderJobDescription,
    trainingLog: renderTrainingLog,
    appraisalLetter: renderAppraisalLetter,
    performanceAppraisal: renderPerformanceAppraisal,
    attachments: renderAttachments,
  };

  const saveCurrent = async () => {
    // Map section key -> form key (addresses bundles three fields, languages bundles languages+hobbies)
    if (sectionKey === 'addresses') {
      await saveSection('presentAddress', form.presentAddress || {});
      await saveSection('permanentAddress', form.permanentAddress || {});
      await saveSection('emergencyContact', form.emergencyContact || {});
      await saveSection('sameAsPresent', !!form.sameAsPresent);
    } else if (sectionKey === 'languages') {
      await saveSection('languages', form.languages || []);
      await saveSection('hobbies', form.hobbies || []);
    } else if (sectionKey === 'otherDetails') {
      await saveSection('pursuingStudies', form.pursuingStudies || {});
      await saveSection('bankAccount', form.bankAccount || {});
      await saveSection('criminalProceedings', form.criminalProceedings || {});
      await saveSection('relativeEmployed', form.relativeEmployed || {});
      await saveSection('references', form.references || []);
    } else if (sectionKey === 'profile') {
      await saveSection('profile', form.profile || {});
      await saveSection('professionalRegs', form.professionalRegs || []);
    } else if (sectionKey === 'attachments') {
      // attachments saved via upload endpoint — nothing to do
      show('Saved');
    } else {
      await saveSection(sectionKey, form[sectionKey] || (Array.isArray(form[sectionKey]) ? [] : {}));
    }
  };

  const next = async () => { await saveCurrent(); if (activeIdx < SECTIONS.length - 1) setActiveIdx(activeIdx + 1); };
  const prev = () => { if (activeIdx > 0) setActiveIdx(activeIdx - 1); };

  const submitStage = record?.submission?.stage || 'draft';
  const readonly = !record?.submission?.editableByStaff && submitStage !== 'rejected';
  const isAdmin = ['owner','admin','super_admin','hr'].includes(currentUser?.role);

  return (
    <div style={modalBg}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        {/* HEADER */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: `1px solid ${theme.border}` }}>
          <FileText size={22} color={theme.primary}/>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, color: theme.navy }}>{record?.fullName || 'New staff'} — Onboarding form</h3>
            <div style={{ fontSize: 12, color: theme.textMuted }}>
              Stage: <b style={{ color: stageColor(submitStage) }}>{stageLabel(submitStage)}</b>
              {record?.formCompletionPct ? ` · ${record.formCompletionPct}% filled` : ''}
            </div>
          </div>
          {msg && <span style={{ fontSize: 12, color: theme.info, fontWeight: 600 }}>{msg}</span>}
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22}/></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '230px 1fr', flex: 1, minHeight: 0 }}>
          {/* SIDE NAV */}
          <div style={{ borderRight: `1px solid ${theme.border}`, overflowY: 'auto', background: theme.sectionCream || '#fffbeb' }}>
            {SECTIONS.map((s, i) => {
              const Ic = s.icon;
              const active = i === activeIdx;
              const sigOff = record?.hrVerification?.sectionSignoff?.[s.key]?.verified;
              return (
                <button key={s.key} onClick={() => setActiveIdx(i)} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 12px', border: 'none',
                  background: active ? 'white' : 'transparent',
                  borderLeft: `3px solid ${active ? theme.primary : 'transparent'}`,
                  cursor: 'pointer', textAlign: 'left',
                  color: active ? theme.navy : theme.textMuted, fontSize: 12.5, fontWeight: active ? 700 : 500,
                }}>
                  <Ic size={14}/>
                  <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</span>
                  {sigOff && <Check size={13} color={theme.success}/>}
                  {s.owner === 'hr' && <span style={{ fontSize: 9, background: theme.warning + '22', color: theme.warning, padding: '2px 5px', borderRadius: 3 }}>HR</span>}
                  {s.owner === 'physician' && <span style={{ fontSize: 9, background: theme.info + '22', color: theme.info, padding: '2px 5px', borderRadius: 3 }}>DR</span>}
                </button>
              );
            })}
          </div>

          {/* BODY */}
          <div style={{ padding: 14, overflowY: 'auto', maxHeight: 'calc(90vh - 160px)' }}>
            <div style={{ marginBottom: 10, color: theme.navy }}>
              <h3 style={{ margin: 0 }}>{section.label}</h3>
            </div>
            {readonly && (
              <div style={{ ...cardStyle, padding: 10, marginBottom: 12, background: theme.warning + '22', color: theme.warning, fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                <AlertCircle size={16}/>
                Form submitted — HR review ke baad edit hoga.
              </div>
            )}
            <fieldset disabled={readonly && !isAdmin} style={{ border: 'none', padding: 0, margin: 0 }}>
              {SECTION_RENDER[sectionKey] ? SECTION_RENDER[sectionKey]() : null}
            </fieldset>
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: `1px solid ${theme.border}`, background: 'white', alignItems: 'center' }}>
          <button onClick={prev} disabled={activeIdx === 0} style={{ ...btnStyle, background: 'white', color: theme.navy, border: `1px solid ${theme.border}`, padding: '8px 14px' }}>
            <ChevronLeft size={14}/> Previous
          </button>
          <button onClick={saveCurrent} disabled={saving} style={{ ...btnStyle, background: theme.info, padding: '8px 14px' }}>
            <Save size={14}/> {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={next} disabled={activeIdx === SECTIONS.length - 1} style={{ ...btnStyle, padding: '8px 14px' }}>
            Save & Next <ChevronRight size={14}/>
          </button>
          <div style={{ flex: 1 }}/>
          {submitStage === 'draft' || submitStage === 'rejected' ? (
            <button onClick={submitForReview} style={{ ...btnStyle, background: theme.success, padding: '8px 14px' }}>
              <Send size={14}/> Submit for HR review
            </button>
          ) : (
            <span style={{ fontSize: 12, color: theme.textMuted }}>Submitted on {record?.submission?.submittedAt ? new Date(record.submission.submittedAt).toLocaleDateString('en-IN') : '—'}</span>
          )}
        </div>
      </div>

      {sigFor && (
        <SignaturePad
          onSave={saveSig}
          onClose={() => setSigFor(null)}
        />
      )}
    </div>
  );
};

function stageColor(s) {
  return {
    draft: theme.textMuted, submitted: theme.info, under_review: theme.warning,
    approved: theme.success, rejected: theme.danger,
  }[s] || theme.textMuted;
}
function stageLabel(s) {
  return { draft: 'Draft', submitted: 'Submitted', under_review: 'Under review', approved: 'Approved', rejected: 'Rejected — needs fixes' }[s] || s;
}

const modalBg = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10 };
const modalBox = {
  background: 'white', borderRadius: 12, width: '100%', maxWidth: 1100,
  height: '90vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 12px 50px rgba(0,0,0,0.35)', overflow: 'hidden',
};

export default OnboardingFormModal;
